import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import {
  ChartBarIcon,
  UserGroupIcon,
  CalendarDaysIcon
} from '@heroicons/react/24/outline';
import { isAdmin } from '../../utils/adminAuth';
import authService from '../../services/AuthServiceWrapper';
import { supabase } from '../../config/supabase';
import { toast } from 'react-hot-toast';

const AdminContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: ${props => props.theme.spacing.xl};
`;

const AdminHeader = styled.h1`
  font-family: 'Space Grotesk', sans-serif;
  font-size: 2rem;
  font-weight: 700;
  color: ${props => props.theme.colors.text.primary};
  margin-bottom: ${props => props.theme.spacing.lg};
  letter-spacing: -0.5px;
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: ${props => props.theme.spacing.lg};
  margin-bottom: ${props => props.theme.spacing.xl};
`;

const StatCard = styled(motion.div)`
  background: ${props => props.theme.colors.surface};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.lg};
  padding: ${props => props.theme.spacing.lg};
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.md};
`;

const StatIcon = styled.div`
  width: 48px;
  height: 48px;
  background: ${props => props.theme.colors.accent}15;
  border-radius: ${props => props.theme.borderRadius.lg};
  display: flex;
  align-items: center;
  justify-content: center;
  
  svg {
    width: 24px;
    height: 24px;
    color: ${props => props.theme.colors.accent};
  }
`;

const StatContent = styled.div`
  flex: 1;
`;

const StatValue = styled.div`
  font-family: 'Space Grotesk', sans-serif;
  font-size: 1.5rem;
  font-weight: 700;
  color: ${props => props.theme.colors.text.primary};
  margin-bottom: ${props => props.theme.spacing.xs};
`;

const StatLabel = styled.div`
  color: ${props => props.theme.colors.text.secondary};
  font-size: ${props => props.theme.typography.caption.fontSize};
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const AdSection = styled.section`
  background: ${props => props.theme.colors.surface};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.lg};
  padding: ${props => props.theme.spacing.lg};
  margin-bottom: ${props => props.theme.spacing.lg};
`;

const SectionTitle = styled.h2`
  font-family: 'Space Grotesk', sans-serif;
  font-size: 1.3rem;
  font-weight: 600;
  color: ${props => props.theme.colors.text.primary};
  margin-bottom: ${props => props.theme.spacing.md};
  letter-spacing: -0.3px;
`;

const AdList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing.md};
`;

const AdItem = styled.div`
  padding: ${props => props.theme.spacing.md};
  background: ${props => props.theme.colors.background};
  border-radius: ${props => props.theme.borderRadius.md};
  border: 1px solid ${props => props.theme.colors.border};
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const AdInfo = styled.div`
  h4 {
    font-weight: 600;
    color: ${props => props.theme.colors.text.primary};
    margin-bottom: ${props => props.theme.spacing.xs};
  }
  
  p {
    color: ${props => props.theme.colors.text.secondary};
    font-size: ${props => props.theme.typography.caption.fontSize};
  }
`;

const AdStats = styled.div`
  display: flex;
  gap: ${props => props.theme.spacing.md};
  font-size: ${props => props.theme.typography.caption.fontSize};
  color: ${props => props.theme.colors.text.secondary};
`;

const AdminAnalytics = () => {
  const [currentUser, setCurrentUser] = useState(authService.currentUser);
  const [analytics, setAnalytics] = useState({
    totalViews: 0,
    totalClicks: 0,
    totalUsers: 0,
    totalShows: 0,
    carouselViews: 0,
    carouselClicks: 0,
    cardViews: 0,
    cardClicks: 0
  });

  useEffect(() => {
    const unsubscribe = authService.addListener((user) => {
      setCurrentUser(user);
    });
    return () => authService.removeListener(unsubscribe);
  }, []);

  useEffect(() => {
    const loadAnalytics = async () => {
      try {
        // Get statistics from existing tables instead of user_stats and ad_analytics
        const [
          { count: totalUsers },
          { data: showStats, error: showStatsError }
        ] = await Promise.all([
          supabase.from('users').select('*', { count: 'exact', head: true }),
          supabase.from('shows').select('id').eq('approval_status', 'approved')
        ]);

        if (showStatsError) throw showStatsError;

        // Since ad_analytics table doesn't exist, use placeholder data
        // TODO: Implement proper ad analytics when advertising service is enabled
        const adStats = {
          total_views: 0,
          total_clicks: 0,
          carousel_views: 0,
          carousel_clicks: 0,
          card_views: 0,
          card_clicks: 0
        };

        setAnalytics({
          totalViews: adStats.total_views || 0,
          totalClicks: adStats.total_clicks || 0,
          totalUsers: totalUsers || 0,
          totalShows: showStats?.length || 0,
          carouselViews: adStats.carousel_views || 0,
          carouselClicks: adStats.carousel_clicks || 0,
          cardViews: adStats.card_views || 0,
          cardClicks: adStats.card_clicks || 0
        });
      } catch (error) {
        console.error('Error loading analytics:', error);
        toast.error('Failed to load analytics data');
      }
    };

    loadAnalytics();
  }, []);

  // Only show to admin users
  if (!isAdmin(currentUser)) {
    return (
      <AdminContainer>
        <AdminHeader>Access Denied</AdminHeader>
        <p>You don't have permission to view this page.</p>
      </AdminContainer>
    );
  }

  const stats = [
    {
      label: 'Total Page Views',
      value: analytics.totalViews.toLocaleString(),
      icon: ChartBarIcon
    },
    {
      label: 'Total Ad Clicks',
      value: analytics.totalClicks.toLocaleString(),
      icon: ChartBarIcon
    },
    {
      label: 'Active Users',
      value: analytics.totalUsers.toLocaleString(),
      icon: UserGroupIcon
    },
    {
      label: 'Total Shows',
      value: analytics.totalShows.toLocaleString(),
      icon: CalendarDaysIcon
    }
  ];

  return (
    <AdminContainer>
      <AdminHeader>📊 SceneFeed Analytics</AdminHeader>
      
      <StatsGrid>
        {stats.map((stat, index) => (
          <StatCard
            key={stat.label}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
          >
            <StatIcon>
              <stat.icon />
            </StatIcon>
            <StatContent>
              <StatValue>{stat.value}</StatValue>
              <StatLabel>{stat.label}</StatLabel>
            </StatContent>
          </StatCard>
        ))}
      </StatsGrid>

      <AdSection>
        <SectionTitle>📈 Carousel Ad Performance</SectionTitle>
        <AdList>
          <AdItem>
            <AdInfo>
              <h4>Default Anti-Algorithm Message</h4>
              <p>Slot 1 • Always visible</p>
            </AdInfo>
            <AdStats>
              <span>{Math.floor(analytics.carouselViews * 0.4).toLocaleString()} views</span>
              <span>{Math.floor(analytics.carouselClicks * 0.3).toLocaleString()} clicks</span>
            </AdStats>
          </AdItem>
          
          <AdItem>
            <AdInfo>
              <h4>Support Underground Music</h4>
              <p>Slot 2 • Placeholder</p>
            </AdInfo>
            <AdStats>
              <span>{Math.floor(analytics.carouselViews * 0.35).toLocaleString()} views</span>
              <span>{Math.floor(analytics.carouselClicks * 0.4).toLocaleString()} clicks</span>
            </AdStats>
          </AdItem>
          
          <AdItem>
            <AdInfo>
              <h4>Record Label Spotlight</h4>
              <p>Slot 3 • Placeholder</p>
            </AdInfo>
            <AdStats>
              <span>{Math.floor(analytics.carouselViews * 0.25).toLocaleString()} views</span>
              <span>{Math.floor(analytics.carouselClicks * 0.3).toLocaleString()} clicks</span>
            </AdStats>
          </AdItem>
        </AdList>
      </AdSection>

      <AdSection>
        <SectionTitle>🎯 Show Card Ad Performance</SectionTitle>
        <AdList>
          <AdItem>
            <AdInfo>
              <h4>Crimson Void - Digital Decay</h4>
              <p>Sample band ad • Appears every 9th card</p>
            </AdInfo>
            <AdStats>
              <span>{Math.floor(analytics.cardViews * 0.4).toLocaleString()} views</span>
              <span>{Math.floor(analytics.cardClicks * 0.45).toLocaleString()} clicks</span>
            </AdStats>
          </AdItem>
          
          <AdItem>
            <AdInfo>
              <h4>Underground Collective</h4>
              <p>Sample venue ad • Appears every 9th card</p>
            </AdInfo>
            <AdStats>
              <span>{Math.floor(analytics.cardViews * 0.35).toLocaleString()} views</span>
              <span>{Math.floor(analytics.cardClicks * 0.35).toLocaleString()} clicks</span>
            </AdStats>
          </AdItem>
          
          <AdItem>
            <AdInfo>
              <h4>Riot Records</h4>
              <p>Sample label ad • Appears every 9th card</p>
            </AdInfo>
            <AdStats>
              <span>{Math.floor(analytics.cardViews * 0.25).toLocaleString()} views</span>
              <span>{Math.floor(analytics.cardClicks * 0.2).toLocaleString()} clicks</span>
            </AdStats>
          </AdItem>
        </AdList>
      </AdSection>
    </AdminContainer>
  );
};

export default AdminAnalytics; 